import React from 'react';
import { ISource } from '@stores/source';
import { TAB_NAMES } from '@components/sources/source-view/utils';
import { DIRECTORY_TABS } from '@components/common/constants';
import {
  AccountValidator,
  ConfigurationComponentProps,
  CredentialsAuth,
  DirectoryTab,
  GetWebhookUrlOptions,
  ResourcesComponentProps,
  SourceCategory,
} from './sourceCategory';
import { WebhookConfigure } from '@components/sources/source/webhookSource/webhookConfigure';
import { IDestination } from '@stores/destination';
import { IFeaturesStore } from '@stores/features';

const Unimplemented = 'Method not implemented';

class EventStreamSource extends SourceCategory {
  override isNewConnectionAllowed() {
    return true;
  }

  override isCreateAllowed(_definitionName: string, _featuresStore: IFeaturesStore) {
    return true;
  }

  override getSunsetReason(_definitionName: string, _featuresStore: IFeaturesStore) {
    return null;
  }

  override getWebhookUrl({
    sourceDefinition,
    writeKey,
    dataPlaneUrl = '<DATAPLANE_URL>',
  }: GetWebhookUrlOptions) {
    if (
      sourceDefinition.category === 'webhook' &&
      sourceDefinition.createFlow !== 'managed-webhook'
    ) {
      return `${dataPlaneUrl.trim()}/v1/webhook?writeKey=${writeKey}`;
    }
    return null;
  }

  override showGeolocationSettings() {
    return true;
  }

  override validateAccount(_: AccountValidator): Promise<string | undefined> {
    throw new Error('Method not implemented.');
  }

  override getAccountsEndpoint(_?: string): string {
    throw Error('Method not supported');
  }

  override getSourceType(sourceDef: { name: string }) {
    switch (sourceDef.name.toLowerCase()) {
      case 'javascript':
        return 'web';
      case 'android_kotlin':
        return 'androidKotlin';
      case 'ios_swift':
        return 'iosSwift';
      case 'android':
      case 'ios':
      case 'unity':
      case 'reactnative':
      case 'flutter':
      case 'cordova':
      case 'amp':
      case 'shopify':
        return sourceDef.name.toLowerCase();
      default:
        return 'cloud';
    }
  }

  /**
   * For event stream sources, we allow multiple sources to connect to the same destination
   * as long as the destination has only event-stream and/or extract sources connected to it.
   */
  override isDestinationAllowedForConnection(destination: IDestination) {
    return destination.sources.every((source) =>
      ['event', 'extract'].includes(source.sourceCategory.category),
    );
  }

  override handleSetup(_: boolean, __: boolean, create: () => void) {
    create();
    return true;
  }

  override isPartOf(tab: DirectoryTab) {
    return tab === DIRECTORY_TABS.EVENT_STREAMS;
  }

  override getAuthComponent(_: CredentialsAuth): JSX.Element {
    throw new Error(Unimplemented);
  }

  override getTabs(source: ISource, featuresStore: IFeaturesStore) {
    const isSdkSource = !!source.sourceDef.options?.sdkExecutionEnvironment;
    const alertsFeatureEnabled = featuresStore.has('NOTIFICATION_SUBSCRIPTIONS');

    return [
      ...(isSdkSource ? [TAB_NAMES.SETUP] : []),
      TAB_NAMES.DESTINATIONS,
      TAB_NAMES.EVENTS,
      TAB_NAMES.SETTINGS,
      ...(alertsFeatureEnabled ? [TAB_NAMES.ALERTS] : []),
    ];
  }

  override getResourcesComponent(_: ResourcesComponentProps): JSX.Element {
    throw new Error(Unimplemented);
  }

  override getConfigurationComponent(props: ConfigurationComponentProps): JSX.Element {
    const { source, onChange, disabled } = props;
    const { sourceDef, config } = source;
    return (
      <WebhookConfigure
        initialSettings={Object.keys(config || {}).length > 0 ? config : undefined}
        onSettingsChange={onChange}
        sourceDefinition={sourceDef!}
        disabled={disabled}
      />
    );
  }

  override getSyncs(_: ISource): JSX.Element | null {
    throw new Error(Unimplemented);
  }
}

/* eslint-disable import/no-default-export */
export default EventStreamSource;
